import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  alpha,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import format from "date-fns/format";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpIcon from "@mui/icons-material/Help";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { setAlert } from "../../features/slices/alertSlice";
import * as Constant from "../../features/constant";
import {
  useWithdrawMutation,
  useGetWithdrawDetailsQuery,
} from "../../features/api/transactionApiSlice";
import { useGetPromotionRecordMutation } from "../../features/api/bonusApiSlice";
import {
  useGetBankAccountsQuery,
  usePlayerBanksMutation,
  useGetWithdrawalDescQuery,
  useGetProfileQuery,
} from "../../features/api/userApiSlice";

const NAMED_COLORS = { white: "#ffffff", black: "#000000" };
const safeAlpha = (color, value) => {
  try {
    return alpha(NAMED_COLORS[String(color).toLowerCase()] || color, value);
  } catch (e) {
    return color;
  }
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const maskAccount = (number) => {
  const str = String(number || "");
  return str.length > 4 ? `****${str.slice(-4)}` : str;
};

const Withdrawal2 = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();

  const isLightTheme = process.env.REACT_APP_IS_LIGHT_THEME === "true";
  const bankAccountUseMobile = process.env.REACT_APP_BANK_ACCOUNT_USE_MOBILE === "true";
  const bankBsbEnabled = process.env.REACT_APP_BANK_BSB_ENABLED === "true";
  const currency = process.env.REACT_APP_CURRENCY_CODE;

  const textColor = process.env.REACT_APP_COLOR_TEXT || (isLightTheme ? "#222" : "white");
  const mutedColor = safeAlpha(textColor, 0.6);
  const accent = theme.palette.primary.main;
  const borderColor = theme.palette.button?.faded?.main || safeAlpha(textColor, 0.25);
  const cardBg = isLightTheme ? "#f2f4f7" : safeAlpha("#ffffff", 0.05);
  const disabledBg = isLightTheme ? "#eceef1" : safeAlpha("#ffffff", 0.08);

  const [openTnc, setOpenTnc] = useState(false);
  const [optionImageFailed, setOptionImageFailed] = useState(false);
  const [selectedBank, setSelectedBank] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [tempBank, setTempBank] = useState({});
  const [isLock, setIsLock] = useState(true);
  const [totalTurnover, setTotalTurnover] = useState(0);
  const [validTurnover, setValidTurnover] = useState(0);
  const [withdrawalMinRolloverAmount, setWithdrawalMinRolloverAmount] = useState(0);

  // Same inputs as Withdrawal; fall back to fetching when opened without router state.
  const passedWithdrawDetails = props.withdrawDetails || location.state?.withdrawDetails;
  const passedUser = props.user || location.state?.user;
  const { data: fallbackWithdrawDetails } = useGetWithdrawDetailsQuery(undefined, { skip: !!passedWithdrawDetails });
  const { data: fallbackUser } = useGetProfileQuery(undefined, { skip: !!passedUser });
  const withdrawDetails = passedWithdrawDetails || fallbackWithdrawDetails?.result;
  const user = passedUser || fallbackUser?.result;

  // Promotion records over the last 7 days drive the turnover lock, as in Withdrawal.
  const [dateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 7);
    return { startDate, endDate };
  });

  const { data: bankDetails, isSuccess: isBankDetailsSuccess } = useGetBankAccountsQuery();
  const { data: withdrawDesc } = useGetWithdrawalDescQuery();
  const [promotionRecord, result] = useGetPromotionRecordMutation();
  const [playerBanks] = usePlayerBanksMutation();

  useEffect(() => {
    promotionRecord({
      criteria: {
        dateFrom: format(dateRange.startDate, "dd-MM-yyyy"),
        dateTo: format(dateRange.endDate, "dd-MM-yyyy"),
        status: "ALL",
      },
      offset: 0,
    }).unwrap();
  }, []);

  useEffect(() => {
    const content = result?.data?.result?.content || [];
    const summary = content.reduce(
      (acc, item) => {
        acc.totalTurnover += Number(item.turnover || 0);
        acc.totalValidTurnover += Number(item.validTurnOver || 0);
        return acc;
      },
      { totalTurnover: 0, totalValidTurnover: 0 }
    );
    setValidTurnover(summary.totalValidTurnover);
    setTotalTurnover(summary.totalTurnover);
    setWithdrawalMinRolloverAmount(user?.withdrawalRollover);

    const hasOngoing = content.some((item) => item?.status === "ONGOING");
    const hasRemainingRollover = user?.withdrawalRollover > 0 && (user?.currentCredit || 0) < user?.withdrawalRollover;
    setIsLock(hasOngoing || hasRemainingRollover);
  }, [result]);

  // ── Amount limits & validation (unchanged from Withdrawal) ──
  const hasMaxWithdraw = user?.maxWithdraw != null && !isNaN(user.maxWithdraw) && user.maxWithdraw > 0;
  const hasMinWithdraw = user?.minWithdraw != null && !isNaN(user.minWithdraw) && user.minWithdraw > 0;
  const maxAmount = hasMaxWithdraw ? user.maxWithdraw : user?.currentCredit;
  const minAmount = hasMinWithdraw ? user.minWithdraw : withdrawDetails?.withdrawalDetails?.minimum;

  let amountSchema = yup
    .number()
    .typeError(t("validation.amountonly"))
    .required(t("validation.amountisrequired"));
  if (!hasMaxWithdraw && minAmount != null && !isNaN(minAmount)) {
    amountSchema = amountSchema.min(minAmount, `Min ${minAmount}`);
  }
  if (maxAmount != null && !isNaN(maxAmount) && maxAmount > 0) {
    amountSchema = amountSchema.max(maxAmount, `Max ${maxAmount}`);
  }

  const withdrawalSchema = yup.object().shape({
    bankCode: yup.string().required(t("validation.bankname")),
    bankAccountName: yup.string().required(t("validation.bankaccname")),
    bankAccountNo: yup
      .string()
      .required(t("validation.bankaccno"))
      .matches(/^[0-9]+$/, t("validation.bankaccnoonlynumbers")),
    bankAccountBsb: bankBsbEnabled
      ? yup.string().nullable().required(t("validation.bankbsb"))
      : yup.string().nullable(),
    amount: amountSchema,
  });

  const { control, handleSubmit, reset, getValues, setValue, clearErrors, watch } = useForm({
    resolver: yupResolver(withdrawalSchema),
  });
  const watchedBankCode = watch("bankCode");

  const applyBank = (bank) => {
    setValue("bankCode", bank?.bankCode);
    setValue("bankAccountName", bank?.bankAccountName);
    setValue("bankAccountNo", bank?.bankAccountNumber);
    setValue("bankAccountBsb", bank?.bankAccountBsb ?? "");
  };

  useEffect(() => {
    if (isBankDetailsSuccess && bankDetails?.result?.data[0]?.bankAccountName) {
      applyBank(bankDetails.result.data[0]);
    }
  }, [isBankDetailsSuccess, bankDetails]);

  const [withdraw, { isLoading, isError, isSuccess, error }] = useWithdrawMutation();

  const submitWithdrawal = async (data) => {
    const newData = {
      amount: data.amount,
      bankAccountName: data.bankAccountName,
      bankAccountNumber: data.bankAccountNo,
      bankCode: data.bankCode,
      ...(bankBsbEnabled && { bankAccountBsb: data.bankAccountBsb }),
    };

    try {
      if (isEditing) {
        await playerBanks(newData).unwrap();
      }
      await withdraw(newData).unwrap();
      navigate(Constant.getPath("/"));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!isLoading && isSuccess) {
      dispatch(setAlert({ isOpen: true, message: t("walletcard.withdrawalpage.successmessage"), variant: "success" }));
      reset();
    }
    if (!isLoading && isError) {
      dispatch(setAlert({ isOpen: true, message: error?.data?.message, variant: "error" }));
      reset();
    }
  }, [isLoading, isSuccess, isError]);

  const startAddBank = () => {
    setTempBank({
      bankCode: getValues("bankCode"),
      bankAccountName: getValues("bankAccountName"),
      bankAccountNumber: getValues("bankAccountNo"),
      bankAccountBsb: getValues("bankAccountBsb"),
    });
    setValue("bankCode", "");
    setValue("bankAccountName", user?.username);
    setValue("bankAccountNo", bankAccountUseMobile ? user?.mobileNumber || "" : "");
    setValue("bankAccountBsb", "");
    setIsEditing(true);
  };

  const cancelAddBank = () => {
    setValue("bankCode", tempBank?.bankCode || "");
    setValue("bankAccountName", tempBank?.bankAccountName || "");
    setValue("bankAccountNo", tempBank?.bankAccountNumber || "");
    setValue("bankAccountBsb", tempBank?.bankAccountBsb || "");
    setTempBank({});
    setIsEditing(false);
    clearErrors(["bankCode", "bankAccountName", "bankAccountNo", "bankAccountBsb"]);
  };

  const withdrawalLimit = [
    { label: "Minimum Amount", value: `${currency} ${minAmount ?? withdrawDetails?.withdrawalDetails?.minimum}` },
    { label: "Maximum Amount", value: `${currency} ${maxAmount ?? withdrawDetails?.withdrawalDetails?.maximum}` },
    { label: "Daily Maximum Amount", value: `${currency} ${withdrawDetails?.withdrawalDetails?.daily_amt}` },
    { label: "Daily Limit", value: `${withdrawDetails?.withdrawalDetails?.daily_limit} Times` },
  ];

  const showTurnover = isLock && totalTurnover > 0 && validTurnover < totalTurnover;
  const showRollover = withdrawalMinRolloverAmount > 0 && (user?.currentCredit || 0) < withdrawalMinRolloverAmount;
  const turnoverPercent = Math.min(((validTurnover || 0) / (totalTurnover || 1)) * 100, 100);
  const rolloverPercent = Math.min(((user?.currentCredit || 0) / (withdrawalMinRolloverAmount || 1)) * 100, 100);
  const savedBanks = bankDetails?.result?.data || [];

  // ── Shared styling ──
  const inputSx = (disabled) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.5,
      color: textColor,
      backgroundColor: disabled ? disabledBg : "transparent",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor },
    "& .MuiOutlinedInput-input": { py: 1.4 },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: process.env.REACT_APP_COLOR_DISABLE || mutedColor,
    },
    "& .MuiFormHelperText-root": { mx: 0 },
    "& .MuiSelect-icon": { color: mutedColor },
  });

  const Label = ({ children, required = true }) => (
    <Typography fontWeight="bold" fontSize="15px" color={textColor} mt={2.5} mb={1}>
      {children}
      {required && (
        <Box component="span" color="error.main" ml={0.3}>
          *
        </Box>
      )}
    </Typography>
  );

  const ProgressRow = ({ label, percent, from, to }) => (
    <Box mt={1}>
      <Box display="flex" justifyContent={label ? "space-between" : "flex-end"} alignItems="center">
        {label && <Typography fontSize="13px" color={textColor}>{label}</Typography>}
        <Typography fontSize="12px" fontWeight="bold" color={textColor}>{percent.toFixed(0)}%</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          my: 0.75,
          height: 5,
          borderRadius: 3,
          backgroundColor: safeAlpha(textColor, 0.12),
          "& .MuiLinearProgress-bar": { backgroundColor: accent, borderRadius: 3 },
        }}
      />
      <Box display="flex" justifyContent="space-between">
        <Typography fontSize="11px" color={mutedColor}>{`${from} ${currency}`}</Typography>
        <Typography fontSize="11px" color={mutedColor}>{`${to} ${currency}`}</Typography>
      </Box>
    </Box>
  );

  const SummaryRow = ({ Icon, iconColor, label, value }) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" py={1}>
      <Box display="flex" alignItems="center" gap={1}>
        <Icon sx={{ fontSize: 18, color: iconColor }} />
        <Typography fontSize="14px" color={textColor}>{label}</Typography>
      </Box>
      <Typography fontSize="14px" fontWeight="bold" color={accent}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        // Desktop inset matches Deposit3's content panel (px: 3) so screens line up.
        px: { xs: 2, sm: 3 },
        pt: { xs: 9, sm: 0 },
        pb: { xs: 14, sm: 2 },
        minHeight: { xs: "100vh", sm: "auto" },
        background: !isLightTheme ? { xs: theme.palette.background.paper, sm: "transparent" } : undefined,
      }}
    >
      {/* ── Terms & conditions ── */}
      <Dialog
        onClose={() => setOpenTnc(false)}
        open={openTnc}
        sx={{ "& .MuiPaper-root": { bgcolor: theme.palette.background.paper, mx: 3, width: "100%" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: accent,
            py: 1,
            px: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography color={process.env.REACT_APP_COLOR_CONTRAST_TEXT || "white"} fontSize="small" fontWeight="bold">
            Terms and condition applied
          </Typography>
          <CloseIcon sx={{ cursor: "pointer" }} onClick={() => setOpenTnc(false)} />
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography dangerouslySetInnerHTML={{ __html: withdrawDesc?.result }} />
        </DialogContent>
      </Dialog>

      {withdrawDesc?.result && (
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Box
            onClick={() => setOpenTnc(true)}
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{ cursor: "pointer" }}
          >
            <HelpIcon sx={{ fontSize: 20, color: accent }} />
            <Typography fontSize="14px" color={accent}>
              Terms and conditions Applied*
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Summary card ── */}
      <Box sx={{ backgroundColor: cardBg, borderRadius: 2, px: 2, py: 1 }}>
        <Box py={0.5}>
          {!isLock ? (
            <Typography fontSize="13px" color="success.main">
              {t("walletcard.withdrawalpage.eligible", "You are eligible to withdraw.")}
            </Typography>
          ) : (
            <>
              <Typography fontSize="13px" fontWeight="bold" color="error.main">
                {t("walletcard.withdrawalpage.locked", "Withdrawal Locked")}
              </Typography>
              <Typography fontSize="12px" color="error.main">
                {t("walletcard.withdrawalpage.noteligible", "You are not eligible to withdraw yet.")}
              </Typography>
            </>
          )}
        </Box>

        <SummaryRow
          Icon={TrendingUpIcon}
          iconColor={accent}
          label={t("walletcard.withdrawalpage.ongoingturnover", "Ongoing Turnover")}
          value={`${currency} ${formatMoney(validTurnover)} / ${formatMoney(totalTurnover)}`}
        />
        {showTurnover && (
          <ProgressRow
            percent={turnoverPercent}
            from={validTurnover || 0}
            to={totalTurnover || 0}
          />
        )}
        {showRollover && (
          <ProgressRow
            label={t("walletcard.withdrawalpage.withdrawalrollover", "Withdrawal Rollover")}
            percent={rolloverPercent}
            from={user?.currentCredit || 0}
            to={withdrawalMinRolloverAmount || 0}
          />
        )}
        {user?.maxWithdraw > 0 && (
          <Box display="flex" justifyContent="space-between" py={1}>
            <Typography fontSize="13px" color={textColor}>
              {t("walletcard.withdrawalpage.maxwithdraw", "Max Withdrawal")}
            </Typography>
            <Typography fontSize="13px" fontWeight="bold" color={textColor}>
              {`${user.maxWithdraw} ${currency}`}
            </Typography>
          </Box>
        )}
        <SummaryRow
          Icon={CheckCircleOutlineIcon}
          iconColor="success.main"
          label={t("walletcard.withdrawalpage.withdrawalbalance", "Withdrawable Balance")}
          value={`${currency} ${formatMoney(user?.currentCredit)}`}
        />
      </Box>

      {/* ── Withdrawal option (bank only) ── */}
      <Label>{t("walletcard.withdrawaloption", "Withdrawal Options")}</Label>
      <Box display="inline-flex" flexDirection="column" alignItems="center" width={76}>
        <Box
          sx={{
            width: 64,
            height: 56,
            borderRadius: 1.5,
            border: `2px solid ${accent}`,
            backgroundColor: cardBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {process.env.REACT_APP_PROFILE && !optionImageFailed ? (
            <Box
              component="img"
              src={`${process.env.REACT_APP_PROFILE}/payment_method_0.png`}
              alt=""
              sx={{ width: 36, height: 36, objectFit: "contain" }}
              onError={() => setOptionImageFailed(true)}
            />
          ) : (
            <AccountBalanceOutlinedIcon sx={{ fontSize: 32, color: accent }} />
          )}
        </Box>
        <Typography fontSize="12px" color={mutedColor} textAlign="center" mt={0.5} lineHeight={1.2}>
          {t("walletcard.depositpage.title", "Bank Transfer")}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(submitWithdrawal)}>
        {/* ── Bank selection ── */}
        <Label>{t("walletcard.withdrawalpage.bankname", "Bank Name")}</Label>
        {!isEditing ? (
          <Controller
            name="bankCode"
            control={control}
            defaultValue=""
            render={({ fieldState: { error: fieldError } }) => (
          <TextField
            select
            fullWidth
            size="small"
            disabled={isLock || savedBanks.length === 0}
            value={savedBanks.length > 0 && savedBanks[selectedBank] ? selectedBank : ""}
            onChange={(e) => {
              const index = Number(e.target.value);
              setSelectedBank(index);
              applyBank(savedBanks[index]);
              clearErrors(["bankCode", "bankAccountName", "bankAccountNo", "bankAccountBsb"]);
            }}
            error={!!fieldError}
            helperText={fieldError?.message}
            SelectProps={{
              displayEmpty: true,
              renderValue: (index) =>
                index === "" || !savedBanks[index] ? (
                  <Typography fontSize="14px" color={mutedColor}>
                    {t("walletcard.withdrawalpage.selectbank", "Please Select")}
                  </Typography>
                ) : (
                  `${savedBanks[index].bankCode} · ${maskAccount(savedBanks[index].bankAccountNumber)}`
                ),
            }}
            sx={inputSx(isLock)}
          >
            {savedBanks.map((bank, index) => (
              <MenuItem key={`${bank.bankCode}-${bank.bankAccountNumber}`} value={index}>
                <Box display="flex" flexDirection="column">
                  <Typography fontSize="14px" fontWeight="bold">{bank.bankCode}</Typography>
                  <Typography fontSize="12px" sx={{ opacity: 0.75 }}>
                    {bank.bankAccountName} · {bank.bankAccountNumber}
                    {bankBsbEnabled && bank.bankAccountBsb ? ` · BSB ${bank.bankAccountBsb}` : ""}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
            )}
          />
        ) : (
          <Controller
            name="bankCode"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
              <TextField
                select
                fullWidth
                size="small"
                value={value || ""}
                onChange={onChange}
                error={!!fieldError}
                helperText={fieldError?.message}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    selected || (
                      <Typography fontSize="14px" color={mutedColor}>
                        {t("walletcard.withdrawalpage.selectbank", "Please Select")}
                      </Typography>
                    ),
                }}
                sx={inputSx(false)}
              >
                {(bankDetails?.result?.bankConfigs || []).map((item) => (
                  <MenuItem key={item.bankCode} value={item.bankCode}>
                    {item.bankCode}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        )}

        {watchedBankCode === "ESEWA" && (
          <Typography color={textColor} variant="body2" mt={1}>
            {t("walletcard.withdrawalpage.esewaWarning")}
            <br />
            {t("walletcard.withdrawalpage.esewaWarning2")}
          </Typography>
        )}

        {/* ── Add bank / cancel ── */}
        {!isEditing ? (
          <Button
            fullWidth
            variant="contained"
            disabled={isLock}
            onClick={startAddBank}
            sx={{
              mt: 3,
              py: 1.1,
              borderRadius: 1.5,
              boxShadow: "none",
              textTransform: "none",
              fontSize: "15px",
              backgroundColor: safeAlpha(accent, 0.55),
              "&:hover": { backgroundColor: safeAlpha(accent, 0.7), boxShadow: "none" },
            }}
          >
            {t("walletcard.withdrawalpage.addbank", "Add Bank")}
          </Button>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            onClick={cancelAddBank}
            sx={{ mt: 3, py: 1.1, borderRadius: 1.5, textTransform: "none", fontSize: "15px", color: textColor }}
          >
            {t("walletcard.withdrawalpage.cancel", "Cancel")}
          </Button>
        )}

        {/* ── Account fields ── */}
        <Label>{t("walletcard.withdrawalpage.bankaccountname", "Bank Account Name")}</Label>
        <Controller
          name="bankAccountName"
          control={control}
          defaultValue=""
          render={({ field: { value }, fieldState: { error: fieldError } }) => (
            <TextField
              fullWidth
              size="small"
              disabled
              value={value || ""}
              error={!!fieldError}
              helperText={fieldError?.message}
              sx={inputSx(true)}
            />
          )}
        />

        <Label>{t("walletcard.withdrawalpage.bankaccountnumber", "Bank Account Number")}</Label>
        <Controller
          name="bankAccountNo"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => {
            const disabled = !isEditing || bankAccountUseMobile;
            return (
              <TextField
                fullWidth
                size="small"
                disabled={disabled}
                value={value || ""}
                onChange={onChange}
                placeholder="Exp : 1234567"
                inputProps={{ inputMode: "numeric" }}
                error={!!fieldError}
                helperText={fieldError?.message}
                sx={inputSx(disabled)}
              />
            );
          }}
        />

        {bankBsbEnabled && (
          <>
            <Label>{t("walletcard.withdrawalpage.bankaccountbsb", "BSB")}</Label>
            <Controller
              name="bankAccountBsb"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
                <TextField
                  fullWidth
                  size="small"
                  disabled={!isEditing}
                  value={value || ""}
                  onChange={onChange}
                  error={!!fieldError}
                  helperText={fieldError?.message}
                  sx={inputSx(!isEditing)}
                />
              )}
            />
          </>
        )}

        {/* ── Amount ── */}
        <Label>{t("walletcard.withdrawalpage.amount", "Amount")}</Label>
        <Controller
          name="amount"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => (
            <TextField
              fullWidth
              size="small"
              disabled={isLock}
              value={value ?? ""}
              onChange={onChange}
              placeholder={`MIN: ${minAmount || 50} / MAX: ${maxAmount ?? withdrawDetails?.withdrawalDetails?.maximum ?? "-"}`}
              inputProps={{ inputMode: "decimal" }}
              error={!!fieldError}
              helperText={fieldError?.message}
              sx={inputSx(isLock)}
            />
          )}
        />
        <Box display="flex" alignItems="center" gap={0.75} mt={1}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: mutedColor }} />
          <Typography fontSize="12px" color={mutedColor}>
            Daily Maximum Amount: {currency} {withdrawDetails?.withdrawalDetails?.daily_amt ?? "-"} / Daily Limit:{" "}
            {withdrawDetails?.withdrawalDetails?.daily_limit ?? "-"} Times
          </Typography>
        </Box>
        <Typography fontSize="14px" color={textColor} mt={1.5}>
          {t("walletcard.withdrawalpage.withdrawalbalance", "Withdrawable Balance")}:{" "}
          <Box component="span" color={accent}>
            {currency} {formatMoney(user?.currentCredit)}
          </Box>
        </Typography>

        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={isLoading || isLock}
          sx={{
            mt: 2,
            py: 1.2,
            borderRadius: 1.5,
            boxShadow: "none",
            textTransform: "none",
            fontSize: "15px",
            fontWeight: "bold",
          }}
        >
          {t("walletcard.submit", "Submit")}
        </Button>
      </form>

      {/* ── Withdrawal limit ── */}
      <Box sx={{ backgroundColor: cardBg, borderRadius: 2, px: 2, py: 1.5, mt: 3 }}>
        <Typography fontSize="14px" fontWeight="bold" color={textColor} mb={0.75}>
          {t("walletcard.withdrawalpage.withdrawalLimit", "Withdrawal Limit")}
        </Typography>
        {withdrawalLimit.map((item) => (
          <Box key={item.label} display="flex" justifyContent="space-between" py={0.25}>
            <Typography fontSize="12px" color={mutedColor}>{item.label}</Typography>
            <Typography fontSize="12px" color={textColor}>{item.value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Withdrawal2;
